import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  CreditCard,
  Globe2,
  Heart,
  Lock,
  MessageSquare,
  Network,
  Quote,
  Rocket,
  Search,
  Settings,
  Sparkles,
  TrendingDown,
  Users,
  Wrench,
  Zap,
} from "lucide-react";

export const Route = createFileRoute("/proposals/legal/agente/2026")({
  component: ProposalPage,
  head: () => ({
    meta: [
      { title: "Propuesta AgentE 2026 — Asesorías, notarías, abogados y procuradores | PULSO33" },
      {
        name: "description",
        content:
          "Propuesta comercial AgentE by PULSO33 para despachos legales en 2026: web + reservas + pagos + CRM + IA que vende + blog automático. Desde 125 € + IVA / mes.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "Propuesta AgentE 2026 — PULSO33" },
      {
        property: "og:description",
        content:
          "El sistema operativo de tu despacho. Calidad de despacho grande, precio imbatible.",
      },
    ],
  }),
});

const COMPARISON: Array<[string, string, string]> = [
  ["Mantenimiento", "Actualizar 15 plugins cada mes (y rezar)", "Un solo proveedor. Sin sorpresas."],
  ["Seguridad", "El plugin desactualizado: vía nº1 de hackeos", "Infraestructura monitorizada 24/7"],
  ["Reservas y pagos", "Bookly + WooCommerce + Stripe + extensiones", "Agenda, cobro y .ics ya integrados"],
  ["CRM", "Plugin extra o HubSpot aparte", "CRM nativo, cada cliente con su historial"],
  ["IA y SEO", "Widgets sueltos + Yoast a mano", "Agente IA y SEO técnico de fábrica"],
  ["Tus datos", "Repartidos en WP, Stripe, Mailchimp, Sheets", "Todo centralizado y exportable"],
];

const SECTIONS = [
  { n: "01", t: "Apertura" },
  { n: "02", t: "Por qué existimos" },
  { n: "03", t: "El problema" },
  { n: "04", t: "La solución" },
  { n: "05", t: "Diseñado para vender" },
  { n: "06", t: "Contenido que posiciona" },
  { n: "07", t: "El flujo que trabaja solo" },
  { n: "08", t: "Servicios a precio cerrado" },
  { n: "09", t: "Una plataforma" },
  { n: "10", t: "Comparación honesta" },
  { n: "11", t: "Sin letra pequeña" },
  { n: "12", t: "Direct33.com" },
  { n: "13", t: "La oferta 2026" },
  { n: "14", t: "Empieza hoy" },
];

function ProposalPage() {
  return (
    <Layout>
      <article className="relative overflow-hidden bg-background text-foreground">
        {/* Floating index */}
        <aside className="hidden xl:block fixed left-6 top-1/2 -translate-y-1/2 z-40">
          <ol className="space-y-1.5 text-[10px] font-mono text-muted-foreground/60">
            {SECTIONS.map((s) => (
              <li key={s.n}>
                <a href={`#s${s.n}`} className="flex items-center gap-2 hover:text-primary transition-colors">
                  <span className="w-6 text-right">{s.n}</span>
                  <span className="h-px w-3 bg-current opacity-50" />
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">{s.t}</span>
                </a>
              </li>
            ))}
          </ol>
        </aside>

        {/* 01 — HERO */}
        <Section id="s01" className="pt-32 pb-24 md:pt-44 md:pb-36">
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-1/4 -left-32 h-[500px] w-[500px] rounded-full bg-primary/20 blur-[140px]" />
            <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-info/15 blur-[120px]" />
          </div>

          <Kicker>Propuesta comercial · 2026</Kicker>
          <div className="mt-4 mb-10 flex items-center gap-3 text-xs font-mono tracking-[0.25em] uppercase text-muted-foreground">
            <span>Asesorías</span><Dot /><span>Notarías</span><Dot /><span>Abogados</span><Dot /><span>Procuradores</span>
          </div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
            No es una web.<br />
            Es el{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-primary-foreground px-3">sistema operativo</span>
              <span className="absolute inset-0 bg-primary -skew-x-6" />
            </span>
            <br />de tu despacho.
          </h1>

          <p className="mt-10 max-w-2xl text-xl md:text-2xl text-muted-foreground leading-relaxed">
            Una sola plataforma que <strong className="text-foreground">capta clientes, los agenda, los cobra y los organiza</strong>. Mientras tú duermes.
          </p>

          <div className="mt-14 inline-flex items-center gap-4 rounded-full border border-primary/30 bg-primary/5 px-6 py-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="text-sm md:text-base">AgentE — tu primer empleado digital, by PULSO33</span>
          </div>
        </Section>

        {/* 02 — WHY */}
        <Section id="s02" tone="surface">
          <Eyebrow n="02" label="Por qué existimos" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            Un despacho pequeño merece la{" "}
            <span className="text-primary">misma tecnología</span> que uno grande.
          </h2>
          <p className="mt-8 max-w-2xl text-lg md:text-xl text-muted-foreground">
            Tú estudiaste para asesorar, defender y dar fe. No para pelearte con 8 herramientas que no se hablan entre sí.
          </p>

          <div className="mt-16 flex items-center gap-6">
            <Heart className="h-10 w-10 text-primary" />
            <Pulse />
            <p className="text-base md:text-lg italic text-foreground/80">
              Tu negocio debería trabajar para ti. No al revés.
            </p>
          </div>
        </Section>

        {/* 03 — PROBLEM */}
        <Section id="s03">
          <Eyebrow n="03" label="El problema real" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            Hoy pierdes clientes y horas{" "}
            <span className="relative">
              sin darte cuenta.
              <span className="absolute left-0 right-0 -bottom-1 h-1 bg-destructive" />
            </span>
          </h2>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {[
              { i: Wrench, t: "8 herramientas sueltas", d: "Web por un lado, agenda por otro, cobros, emails, CRM… Saltas de una a otra y algo siempre se rompe." },
              { i: Lock, t: "Cierras y dejas de vender", d: "Cuando apagas la luz, tu negocio se apaga contigo. El cliente que entra a medianoche no encuentra a nadie." },
              { i: TrendingDown, t: "Horas que no facturas", d: "Copiar datos, contestar lo mismo, agendar a mano. Tiempo que no vuelve y que no te pagan." },
            ].map((c) => (
              <div key={c.t} className="group relative p-8 rounded-2xl border border-border bg-card hover:border-destructive/40 transition-colors">
                <c.i className="h-10 w-10 text-destructive/70 mb-6" />
                <h3 className="font-display text-2xl mb-3">{c.t}</h3>
                <p className="text-muted-foreground leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 max-w-3xl border-l-4 border-primary pl-8 py-4">
            <p className="text-xl md:text-2xl font-display">
              El coste de no actuar no aparece en ninguna factura.
            </p>
            <p className="mt-3 text-muted-foreground">
              Pero está ahí: cada lead frío, cada hora perdida, cada cliente que se fue con quien sí le respondió.
            </p>
          </div>
        </Section>

        {/* 04 — SOLUTION */}
        <Section id="s04" tone="primary">
          <Eyebrow n="04" label="La solución" dark />
          <h2 className="font-display text-5xl md:text-7xl leading-[1] max-w-5xl mt-6 text-primary-foreground">
            Conoce a <span className="underline decoration-4 underline-offset-8">AgentE</span>.<br />
            Tu primer empleado digital.
          </h2>
          <p className="mt-8 max-w-2xl text-xl text-primary-foreground/85 leading-relaxed">
            No es un programa más. Es un trabajador incansable que atiende, vende, organiza y cobra por ti.
            <strong className="block mt-2">24 horas. 7 días. Sin sueldo extra, sin bajas, sin descanso.</strong>
          </p>

          <div className="mt-14 grid sm:grid-cols-3 gap-4">
            {[
              { i: Zap, t: "Vende mientras duermes" },
              { i: Globe2, t: "Todo en un solo sitio" },
              { i: Rocket, t: "Listo desde el día 1" },
            ].map((c) => (
              <div key={c.t} className="flex items-center gap-4 rounded-xl bg-primary-foreground/10 backdrop-blur p-5 border border-primary-foreground/15">
                <c.i className="h-6 w-6 text-primary-foreground" />
                <span className="font-medium text-primary-foreground">{c.t}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* 05 — DESIGNED TO SELL */}
        <Section id="s05">
          <Eyebrow n="05" label="Diseñado para vender" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            AgentE escribe el <span className="text-primary">código invisible</span> que te trae clientes.
          </h2>
          <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
            Hay una parte de tu web que tus clientes no ven, pero que Google, Bing y las IA sí leen. AgentE la optimiza sola, todo el tiempo, para que te recomienden a ti.
          </p>

          <div className="mt-12 flex flex-wrap gap-3">
            {["Google", "Bing", "ChatGPT", "Gemini", "Perplexity", "Copilot"].map((b) => (
              <span key={b} className="px-5 py-2.5 rounded-full bg-card border border-border font-mono text-sm">
                {b}
              </span>
            ))}
          </div>

          <div className="mt-16 p-10 rounded-3xl bg-gradient-to-br from-card to-surface-muted border border-border">
            <Quote className="h-8 w-8 text-primary mb-4" />
            <p className="font-display text-2xl md:text-3xl leading-tight">
              Cuando alguien pregunta <em>"¿qué asesoría me recomiendas?"</em>,<br />
              queremos que la respuesta seas tú.
            </p>
          </div>
        </Section>

        {/* 06 — CONTENT */}
        <Section id="s06" tone="surface">
          <Eyebrow n="06" label="Contenido que posiciona" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            Artículos perfectos para tu negocio.<br />
            En <span className="text-primary">segundos</span>, no en semanas.
          </h2>

          <div className="mt-12 grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Un blog activo es lo que convence a Google y a las IA de que eres un experto. Pero escribir cuesta tiempo que no tienes.
              </p>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                AgentE redacta artículos profesionales, pensados para tu sector y tus clientes, con su SEO ya resuelto.
              </p>
              <ul className="mt-8 space-y-3">
                {["Listo para publicar de un vistazo", "Tu voz, tu sector, tus servicios", "Optimizado para buscadores e IA"].map((t) => (
                  <li key={t} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            <MockEditor />
          </div>
        </Section>

        {/* 07 — FLOW */}
        <Section id="s07">
          <Eyebrow n="07" label="El flujo que trabaja solo" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            Capta, agenda, cobra y organiza.<br />
            <span className="text-primary">Sin que muevas un dedo.</span>
          </h2>

          <div className="mt-16 grid md:grid-cols-4 gap-4">
            {[
              { n: "1", t: "Llega", d: "Te encuentra en Google y entra a tu web.", i: Search },
              { n: "2", t: "Reserva", d: "Elige hora real de tu agenda y paga online.", i: Calendar },
              { n: "3", t: "Cobra", d: "Stripe procesa. Confirmación y .ics al instante.", i: CreditCard },
              { n: "4", t: "CRM", d: "Entra solo a tu ficha de cliente, con su historial.", i: Users },
            ].map((s, i) => (
              <div key={s.n} className="relative p-6 rounded-2xl bg-card border border-border">
                <div className="absolute -top-4 -left-4 h-12 w-12 rounded-full bg-primary text-primary-foreground font-display text-xl flex items-center justify-center shadow-lg">
                  {s.n}
                </div>
                {i < 3 && (
                  <ArrowRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 h-6 w-6 text-primary/60" />
                )}
                <s.i className="h-7 w-7 text-primary mt-6 mb-4" />
                <h3 className="font-display text-xl mb-2">{s.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* 08 — SERVICES */}
        <Section id="s08" tone="surface">
          <Eyebrow n="08" label="Tus servicios, listos para vender" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            Crea servicios a <span className="text-primary">precio cerrado</span> y véndelos online.
          </h2>
          <p className="mt-8 max-w-2xl text-lg text-muted-foreground">
            Define tus paquetes una vez (una gestión, una consulta, un trámite urgente) con su precio claro. Tus clientes los contratan desde la web, suben su documentación y pagan. Sin ir y venir de emails.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { t: "Consulta exprés", p: "60 €", d: "30 min · videollamada", c: "bg-info/10 border-info/30" },
              { t: "Trámite estándar", p: "180 €", d: "Gestión completa · 7 días", c: "bg-primary/10 border-primary/40 ring-2 ring-primary/20" },
              { t: "Trámite urgente", p: "320 €", d: "Prioridad máxima · 48 h", c: "bg-destructive/10 border-destructive/30" },
            ].map((s) => (
              <div key={s.t} className={`p-7 rounded-2xl border ${s.c}`}>
                <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{s.t}</p>
                <p className="font-display text-5xl mt-4">{s.p}</p>
                <p className="text-sm text-muted-foreground mt-2">{s.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 inline-flex items-center gap-3 rounded-full bg-card border border-border px-5 py-3 text-sm">
            <Sparkles className="h-4 w-4 text-primary" />
            Y se instala como app en el móvil de tus clientes.
          </div>
        </Section>

        {/* 09 — ONE PLATFORM */}
        <Section id="s09">
          <Eyebrow n="09" label="Una plataforma, todo dentro" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            Treinta pantallas. <span className="text-primary">Cero saltos</span> entre herramientas.
          </h2>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { c: "Operación", items: ["Reservas", "Mensajes", "CRM", "Pagos"] },
              { c: "Marketing", items: ["SEO & Metadatos", "Datos estructurados", "robots / llms.txt", "Checklist SEO/GEO"] },
              { c: "Design", items: ["Theme & Styles", "Site config", "Favicon"] },
              { c: "Settings", items: ["Tenant settings", "Asistente AI", "Equipo", "Mi cuenta"] },
            ].map((g) => (
              <div key={g.c} className="p-6 rounded-2xl bg-card border border-border">
                <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">{g.c}</p>
                <ul className="space-y-2.5">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-center gap-2 text-sm">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* 10 — COMPARISON */}
        <Section id="s10" tone="surface">
          <Eyebrow n="10" label="La comparación honesta" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            No es web bonita vs. web fea.<br />
            Es <span className="text-destructive">20 plugins frágiles</span> vs.{" "}
            <span className="text-primary">una plataforma integrada</span>.
          </h2>

          <div className="mt-12 overflow-hidden rounded-2xl border border-border">
            <div className="grid grid-cols-3 bg-card font-mono text-xs uppercase tracking-widest">
              <div className="p-4 text-muted-foreground">Aspecto</div>
              <div className="p-4 text-destructive">WordPress + Plugins</div>
              <div className="p-4 text-primary">AgentE by PULSO33</div>
            </div>
            {COMPARISON.map(([a, w, p], i) => (
              <div key={a} className={`grid grid-cols-3 ${i % 2 ? "bg-card/40" : "bg-card/20"}`}>
                <div className="p-4 font-medium border-t border-border">{a}</div>
                <div className="p-4 text-sm text-muted-foreground border-t border-border line-through decoration-destructive/40">{w}</div>
                <div className="p-4 text-sm border-t border-border flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /> {p}
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* 11 — NO FINE PRINT */}
        <Section id="s11">
          <Eyebrow n="11" label="Sin letra pequeña" />
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] max-w-4xl mt-6">
            Lo que pidas, lo hacemos. <span className="text-primary">Incluido.</span>
          </h2>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { i: Wrench, t: "El primer mes, todo", d: "Todas las modificaciones, adaptaciones y cambios que quieras durante el primer mes están incluidos. Lo dejamos a tu gusto." },
              { i: Settings, t: "Después, un cambio al mes", d: "Cada mes incluyes un cambio dentro de las funcionalidades. Tu plataforma evoluciona contigo, sin facturas extra." },
              { i: MessageSquare, t: "Un solo socio tecnológico", d: "Nada de coordinar 5 proveedores. Una sola llamada, un solo equipo, todo resuelto." },
            ].map((c) => (
              <div key={c.t} className="p-8 rounded-2xl bg-card border border-border">
                <c.i className="h-10 w-10 text-primary mb-5" />
                <h3 className="font-display text-2xl mb-3">{c.t}</h3>
                <p className="text-muted-foreground leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          <p className="mt-14 text-2xl md:text-3xl font-display max-w-3xl leading-snug">
            Queremos ser el <span className="text-primary">socio tecnológico de referencia</span> de asesorías, notarías, abogados y procuradores.
          </p>
        </Section>

        {/* 12 — DIRECT33 */}
        <Section id="s12" tone="surface">
          <Eyebrow n="12" label="Incluido y gratis para ti" />
          <div className="grid lg:grid-cols-2 gap-12 items-center mt-6">
            <div>
              <h2 className="font-display text-5xl md:text-7xl leading-[1] text-primary">
                Direct33<span className="text-foreground">.com</span>
              </h2>
              <p className="mt-6 text-2xl font-display">El directorio que te trae clientes nuevos.</p>
              <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
                Estamos construyendo una red de profesionales. Te incluimos gratis para que te encuentren nuevos clientes y para que colabores con otros despachos.
              </p>

              <ul className="mt-8 space-y-3">
                {[
                  "Un despacho te envía clientes (y tú a él)",
                  "Comisiones y acuerdos que pactáis libremente",
                  "Expedientes compartidos entre colaboradores",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-3 text-base">
                    <Network className="h-5 w-5 text-primary shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative aspect-square rounded-3xl bg-gradient-to-br from-primary/30 via-card to-info/20 border border-border p-10 flex items-center justify-center overflow-hidden">
              <div className="absolute inset-0 opacity-30">
                <svg viewBox="0 0 400 400" className="w-full h-full">
                  <defs>
                    <radialGradient id="g1">
                      <stop offset="0%" stopColor="currentColor" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                  {Array.from({ length: 24 }).map((_, i) => {
                    const a = (i / 24) * Math.PI * 2;
                    const x = 200 + Math.cos(a) * 140;
                    const y = 200 + Math.sin(a) * 140;
                    return (
                      <g key={i} className="text-primary">
                        <line x1="200" y1="200" x2={x} y2={y} stroke="currentColor" strokeOpacity="0.25" strokeWidth="0.5" />
                        <circle cx={x} cy={y} r="4" fill="currentColor" />
                      </g>
                    );
                  })}
                  <circle cx="200" cy="200" r="20" fill="url(#g1)" className="text-primary" />
                  <circle cx="200" cy="200" r="8" className="fill-primary" />
                </svg>
              </div>
              <div className="relative text-center">
                <p className="font-display text-3xl">Más clientes.<br />Más colaboración.</p>
                <p className="mt-3 text-sm text-muted-foreground">Sin coste adicional.</p>
              </div>
            </div>
          </div>
        </Section>

        {/* 13 — OFFER */}
        <Section id="s13" tone="primary">
          <Eyebrow n="13" label="La oferta 2026" dark />
          <h2 className="font-display text-5xl md:text-7xl leading-[1] max-w-5xl mt-6 text-primary-foreground">
            Calidad de despacho grande. <span className="underline decoration-4 underline-offset-8">Precio imbatible.</span>
          </h2>
          <p className="mt-6 text-xl text-primary-foreground/85 max-w-2xl">
            Antes 250 €/mes. Este 2026 lo bajamos. Y te lo congelamos en cada renovación (solo ajuste IPC).
          </p>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <div className="p-10 rounded-3xl bg-primary-foreground/10 backdrop-blur border border-primary-foreground/15">
              <p className="font-mono text-xs uppercase tracking-widest text-primary-foreground/70">Cuota mensual</p>
              <div className="mt-6 flex items-baseline gap-4">
                <span className="font-display text-3xl line-through text-primary-foreground/40">250 €</span>
                <span className="font-display text-7xl text-primary-foreground">149 €</span>
              </div>
              <p className="mt-3 text-primary-foreground/80 text-sm">+ IVA / mes · precio bloqueado en cada renovación</p>
            </div>

            <div className="relative p-10 rounded-3xl bg-primary-foreground text-foreground border-4 border-primary-foreground shadow-2xl">
              <span className="absolute -top-3 right-6 px-3 py-1 rounded-full bg-foreground text-background text-[10px] font-mono uppercase tracking-widest">
                Mejor opción
              </span>
              <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Pago anual (1 o 2 plazos)</p>
              <div className="mt-6 flex items-baseline gap-3">
                <span className="font-display text-7xl">125 €</span>
                <span className="text-muted-foreground">+ IVA / mes</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">La cifra más baja del mercado por esta calidad.</p>
            </div>
          </div>

          <div className="mt-10 rounded-2xl bg-primary-foreground/10 backdrop-blur border border-primary-foreground/15 p-6 text-primary-foreground">
            <p className="font-medium mb-3">Y todo lo anterior incluido:</p>
            <div className="flex flex-wrap gap-2">
              {["Web", "Reservas", "Pagos", "CRM", "IA que vende", "Blog automático", "App móvil", "Direct33.com"].map((p) => (
                <span key={p} className="px-3 py-1.5 rounded-full bg-primary-foreground/15 text-sm">
                  {p}
                </span>
              ))}
            </div>
          </div>
        </Section>

        {/* 14 — CTA */}
        <Section id="s14" className="pb-32">
          <Eyebrow n="14" label="Empieza hoy" />
          <h2 className="font-display text-5xl md:text-8xl leading-[0.95] mt-6 max-w-5xl">
            Deja de trabajar para tu negocio.<br />
            Haz que <span className="text-primary">trabaje para ti.</span>
          </h2>
          <p className="mt-10 text-xl text-muted-foreground max-w-2xl">
            AgentE ya está listo para captar, vender y organizar por ti. Solo falta que empieces hoy.
          </p>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-105 transition-transform"
            >
              Reserva una demo <ArrowRight className="h-5 w-5" />
            </Link>
            <Link
              to="/products/agente"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-border bg-card hover:border-primary/50 transition-colors"
            >
              Visita PULSO33.com
            </Link>
          </div>

          <div className="mt-20 border-t border-border pt-8 flex items-center justify-between text-xs font-mono text-muted-foreground">
            <span>PULSO33 · Propuesta confidencial · 2026</span>
            <span>14 / 14</span>
          </div>
        </Section>
      </article>
    </Layout>
  );
}

/* ---------- helpers ---------- */

function Section({
  id,
  children,
  tone,
  className = "",
}: {
  id?: string;
  children: React.ReactNode;
  tone?: "surface" | "primary";
  className?: string;
}) {
  const bg =
    tone === "surface"
      ? "bg-surface-muted"
      : tone === "primary"
      ? "bg-primary"
      : "bg-background";
  return (
    <section id={id} className={`relative ${bg} ${className || "py-24 md:py-32"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">{children}</div>
    </section>
  );
}

function Kicker({ children }: { children: React.ReactNode }) {
  return (
    <p className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-primary">
      <span className="h-px w-8 bg-primary" /> {children}
    </p>
  );
}

function Eyebrow({ n, label, dark }: { n: string; label: string; dark?: boolean }) {
  return (
    <div className={`flex items-center gap-4 font-mono text-xs uppercase tracking-[0.3em] ${dark ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
      <span className={`font-display text-2xl ${dark ? "text-primary-foreground" : "text-primary"}`}>{n}</span>
      <span className={`h-px w-12 ${dark ? "bg-primary-foreground/40" : "bg-border"}`} />
      <span>{label}</span>
    </div>
  );
}

function Dot() {
  return <span className="h-1 w-1 rounded-full bg-primary/60" />;
}

function Pulse() {
  return (
    <svg viewBox="0 0 200 40" className="h-10 w-40 text-primary">
      <polyline
        points="0,20 30,20 40,5 50,35 60,10 70,30 80,20 200,20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}

function MockEditor() {
  return (
    <div className="rounded-2xl bg-card border border-border p-6 shadow-2xl">
      <div className="flex items-center gap-2 mb-5">
        <span className="h-3 w-3 rounded-full bg-destructive/60" />
        <span className="h-3 w-3 rounded-full bg-primary/60" />
        <span className="h-3 w-3 rounded-full bg-success/60" />
        <span className="ml-3 font-mono text-xs text-muted-foreground">Editar artículo</span>
      </div>
      <div className="space-y-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Título</p>
          <p className="mt-1 font-display text-lg leading-tight">
            La Factura Electrónica Obligatoria: Guía de Supervivencia para Autónomos y Pymes
          </p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Slug</p>
          <p className="mt-1 font-mono text-xs text-primary truncate">
            /la-factura-electronica-obligatoria-guia-de-supervivencia
          </p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Resumen</p>
          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
            Guía esencial para autónomos y pymes sobre la factura electrónica obligatoria: plazos, diferencias con VeriFactu y cómo evitar sanciones.
          </p>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <span className="px-2 py-1 rounded bg-success/15 text-success text-[10px] font-mono uppercase">SEO 98/100</span>
          <span className="px-2 py-1 rounded bg-primary/15 text-primary text-[10px] font-mono uppercase">Listo</span>
        </div>
      </div>
    </div>
  );
}
